import React, { useEffect, useMemo } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import { Resolver, useForm } from "react-hook-form";
import * as yup from "yup";
import { Alert, Box, Button, Stack, Typography } from "@mui/material";
import ConfigurableFieldRenderer from "./ConfigurableFieldRenderer";
import {
  ConfigurableFieldDefinition,
  ConfigurableFormValue,
  ConfigurableFormValues,
  ResolvedConfigurableField,
} from "../types/configurableUpload";

interface ConfigurableUploadFormProps {
  fields: Array<ConfigurableFieldDefinition | ResolvedConfigurableField>;
  formValues: ConfigurableFormValues;
  formLevelMessages: string[];
  isSubmitting?: boolean;
  isResolving?: boolean;
  pendingFieldKeys?: string[];
  disabled?: boolean;
  onFieldChange: (
    field: ConfigurableFieldDefinition | ResolvedConfigurableField,
    value: ConfigurableFormValue,
  ) => void;
  onSubmit: () => void;
}

const buildValidationSchema = (
  fields: Array<ConfigurableFieldDefinition | ResolvedConfigurableField>,
) => {
  const shape: Record<string, yup.Schema<unknown>> = {};

  fields.forEach((field) => {
    if (!field.visible || field.fieldType === "hidden" || !field.required) {
      return;
    }

    if (field.fieldType === "file") {
      shape[field.key] = yup
        .mixed<File>()
        .test("required-file", `${field.label ?? field.key} is required`, (value) =>
          value instanceof File,
        );
      return;
    }

    if (field.fieldType === "multi_select") {
      shape[field.key] = yup
        .array()
        .of(yup.string().required())
        .min(1, `${field.label ?? field.key} is required`);
      return;
    }

    shape[field.key] = yup
      .string()
      .nullable()
      .required(`${field.label ?? field.key} is required`);
  });

  return yup.object().shape(shape);
};

const ConfigurableUploadForm: React.FC<ConfigurableUploadFormProps> = ({
  fields,
  formValues,
  formLevelMessages,
  isSubmitting = false,
  isResolving = false,
  pendingFieldKeys = [],
  disabled = false,
  onFieldChange,
  onSubmit,
}) => {
  const sortedFields = useMemo(
    () => [...fields].sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
    [fields],
  );
  const validationSchema = useMemo(
    () => buildValidationSchema(sortedFields),
    [sortedFields],
  );

  const { control, handleSubmit, reset } = useForm<ConfigurableFormValues>({
    defaultValues: formValues,
    resolver: yupResolver(validationSchema) as unknown as Resolver<ConfigurableFormValues>,
  });

  useEffect(() => {
    reset(formValues);
  }, [formValues, reset]);

  return (
    <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
      <Stack spacing={3}>
        {formLevelMessages.map((message, index) => (
          <Alert severity="info" key={`${index}-${message}`}>
            {message}
          </Alert>
        ))}
        {sortedFields.map((field) => (
          <ConfigurableFieldRenderer
            key={field.key}
            field={field}
            control={control}
            disabled={disabled || isSubmitting}
            isPending={pendingFieldKeys.includes(field.key)}
            onFieldChange={onFieldChange}
          />
        ))}
        {isResolving && (
          <Typography variant="body2" color="text.secondary">
            Updating form options...
          </Typography>
        )}
        <Button
          type="submit"
          variant="contained"
          size="large"
          disabled={disabled || isSubmitting || isResolving || sortedFields.length === 0}
        >
          {isSubmitting ? "Uploading..." : "Upload"}
        </Button>
      </Stack>
    </Box>
  );
};

export default ConfigurableUploadForm;
